import { lastDayOfMonth } from "date-fns";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import useReportsStore from "~/store/reportsStore";

export default function PeriodFilter({ report = null }) {
  const [open, setOpen] = useState(false);
  const { filters, setFilters, periodOptions } = useReportsStore();
  const initialPeriod =
    (report && filters.periodsByReport?.[report]) || filters.period;
  const [startDate, setStartDate] = useState(initialPeriod?.from_date);
  const [endDate, setEndDate] = useState(initialPeriod?.until_date);
  const [selected, setSelected] = useState(initialPeriod);
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const [baseMonth, setBaseMonth] = useState(new Date().getMonth());
  const [compareMonth, setCompareMonth] = useState(
    (new Date().getMonth() + 11) % 12
  );
  const [baseYear, setBaseYear] = useState(currentYear);
  const [compareYear, setCompareYear] = useState(currentYear);
  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, monthIndex) => ({
        value: monthIndex,
        label: new Intl.DateTimeFormat("en-US", { month: "short" }).format(
          new Date(2020, monthIndex, 1)
        ),
      })),
    []
  );
  const yearOptions = useMemo(
    () =>
      Array.from({ length: 6 }, (_, idx) => {
        const year = currentYear - idx;
        return { value: year, label: String(year) };
      }),
    [currentYear]
  );

  const buildMonthVsMonthPeriod = (
    baseMonthIndex,
    compareMonthIndex,
    baseYearValue,
    compareYearValue
  ) => {
    const baseStart = new Date(baseYearValue, baseMonthIndex, 1);
    const baseEnd = lastDayOfMonth(baseStart);
    const compareStart = new Date(compareYearValue, compareMonthIndex, 1);
    const compareEnd = lastDayOfMonth(compareStart);
    return {
      label: "Month vs Month",
      from: baseStart,
      to: baseEnd,
      compare_from: compareStart,
      compare_to: compareEnd,
    };
  };
  const buildYearVsYearPeriod = (baseYearValue, compareYearValue) => {
    const baseStart = new Date(baseYearValue, 0, 1);
    const baseEnd = new Date(baseYearValue, 11, 31);
    const compareStart = new Date(compareYearValue, 0, 1);
    const compareEnd = new Date(compareYearValue, 11, 31);
    return {
      label: "Year vs Year",
      from: baseStart,
      to: baseEnd,
      compare_from: compareStart,
      compare_to: compareEnd,
    };
  };
  const onChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };
  return (
    <div className="flex flex-row justify-start items-start gap-2 flex-nowrap">
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed top-0 left-0 w-full h-full bg-transparent z-10"
        ></div>
      )}
      <div
        className={`relative flex flex-col text-xs rounded px-1 min-h-10 min-w-24 z-20 border ${
          open ? "border-primary shadow-lg bg-white" : "border-transparent"
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex flex-row justify-between items-center gap-x-2 text-xs cursor-pointer p-1 text-left h-10"
        >
          <div className="flex flex-col">
            <span>Period</span>
            <strong className="text-xs">{selected?.label}</strong>
          </div>
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {open && (
          <div className="absolute left-0 top-full mt-1 w-full flex flex-col justify-start items-start gap-1 py-1 border border-zinc-300 text-zinc-500 bg-white rounded shadow-lg max-h-24 overflow-y-auto">
            {periodOptions.map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  const nextPeriod =
                    option?.label === "Month vs Month"
                      ? buildMonthVsMonthPeriod(
                          baseMonth,
                          compareMonth,
                          baseYear,
                          compareYear
                        )
                      : option?.label === "Year vs Year"
                      ? buildYearVsYearPeriod(baseYear, compareYear)
                      : option;
                  const periodsByReport = {
                    ...(filters.periodsByReport || {}),
                    ...(report ? { [report]: nextPeriod } : {}),
                  };
                  setFilters({
                    ...filters,
                    report: report,
                    periodsByReport,
                  });
                  setOpen(false);
                  setSelected(nextPeriod);
                }}
                className="w-full text-left p-1 hover:text-primary hover:bg-zinc-200 rounded-sm cursor-pointer"
              >
                {option?.label}
              </button>
            ))}
          </div>
        )}
      </div>
      {selected?.label === "Month vs Month" && (
        <div className="flex flex-row items-start gap-2 text-xs">
          <div className="flex flex-col shrink-0">
            <span className="text-[11px] text-zinc-600">Base Month</span>
            <select
              className="h-10 min-w-[104px] border border-zinc-200 rounded-sm bg-white px-2"
              value={baseMonth}
              onChange={(event) => {
                const nextBaseMonth = Number(event.target.value);
                setBaseMonth(nextBaseMonth);
                const nextPeriod = buildMonthVsMonthPeriod(
                  nextBaseMonth,
                  compareMonth,
                  baseYear,
                  compareYear
                );
                setFilters({
                  ...filters,
                  report: report,
                  periodsByReport: {
                    ...(filters.periodsByReport || {}),
                    ...(report ? { [report]: nextPeriod } : {}),
                  },
                });
                setSelected(nextPeriod);
              }}
            >
              {monthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col shrink-0">
            <span className="text-[11px] text-zinc-600">Base Year</span>
            <select
              className="h-10 min-w-[96px] border border-zinc-200 rounded-sm bg-white px-2"
              value={baseYear}
              onChange={(event) => {
                const nextBaseYear = Number(event.target.value);
                setBaseYear(nextBaseYear);
                const nextPeriod = buildMonthVsMonthPeriod(
                  baseMonth,
                  compareMonth,
                  nextBaseYear,
                  compareYear
                );
                setFilters({
                  ...filters,
                  report: report,
                  periodsByReport: {
                    ...(filters.periodsByReport || {}),
                    ...(report ? { [report]: nextPeriod } : {}),
                  },
                });
                setSelected(nextPeriod);
              }}
            >
              {yearOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col shrink-0">
            <span className="text-[11px] text-zinc-600">Compare Month</span>
            <select
              className="h-10 min-w-[128px] border border-zinc-200 rounded-sm bg-white px-2"
              value={compareMonth}
              onChange={(event) => {
                const nextCompareMonth = Number(event.target.value);
                setCompareMonth(nextCompareMonth);
                const nextPeriod = buildMonthVsMonthPeriod(
                  baseMonth,
                  nextCompareMonth,
                  baseYear,
                  compareYear
                );
                setFilters({
                  ...filters,
                  report: report,
                  periodsByReport: {
                    ...(filters.periodsByReport || {}),
                    ...(report ? { [report]: nextPeriod } : {}),
                  },
                });
                setSelected(nextPeriod);
              }}
            >
              {monthOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col shrink-0">
            <span className="text-[11px] text-zinc-600">Compare Year</span>
            <select
              className="h-10 min-w-[96px] border border-zinc-200 rounded-sm bg-white px-2"
              value={compareYear}
              onChange={(event) => {
                const nextCompareYear = Number(event.target.value);
                setCompareYear(nextCompareYear);
                const nextPeriod = buildMonthVsMonthPeriod(
                  baseMonth,
                  compareMonth,
                  baseYear,
                  nextCompareYear
                );
                setFilters({
                  ...filters,
                  report: report,
                  periodsByReport: {
                    ...(filters.periodsByReport || {}),
                    ...(report ? { [report]: nextPeriod } : {}),
                  },
                });
                setSelected(nextPeriod);
              }}
            >
              {yearOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {selected?.label === "Year vs Year" && (
        <div className="flex flex-row items-start gap-2 text-xs">
          <div className="flex flex-col shrink-0">
            <span className="text-[11px] text-zinc-600">Base Year</span>
            <select
              className="h-10 min-w-[96px] border border-zinc-200 rounded-sm bg-white px-2"
              value={baseYear}
              onChange={(event) => {
                const nextBaseYear = Number(event.target.value);
                setBaseYear(nextBaseYear);
                const nextPeriod = buildYearVsYearPeriod(
                  nextBaseYear,
                  compareYear
                );
                setFilters({
                  ...filters,
                  report: report,
                  periodsByReport: {
                    ...(filters.periodsByReport || {}),
                    ...(report ? { [report]: nextPeriod } : {}),
                  },
                });
                setSelected(nextPeriod);
              }}
            >
              {yearOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col shrink-0">
            <span className="text-[11px] text-zinc-600">Compare Year</span>
            <select
              className="h-10 min-w-[96px] border border-zinc-200 rounded-sm bg-white px-2"
              value={compareYear}
              onChange={(event) => {
                const nextCompareYear = Number(event.target.value);
                setCompareYear(nextCompareYear);
                const nextPeriod = buildYearVsYearPeriod(
                  baseYear,
                  nextCompareYear
                );
                setFilters({
                  ...filters,
                  report: report,
                  period: nextPeriod,
                });
                setSelected(nextPeriod);
              }}
            >
              {yearOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      {selected?.label === "Custom" && (
        <div className="flex flex-row justify-between items-center gap-x-2 text-xs cursor-pointer text-left p-1">
          <DatePicker
            className="bg-transparent bg-zinc-50 border text-center border-zinc-200 rounded-sm h-10"
            selected={startDate}
            onChange={onChange}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            rangeSeparator=" to "
          />
        </div>
      )}
    </div>
  );
}
